import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { UpdateChangeListService } from './update-change-list.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-update-change-list',
    templateUrl: './update-change-list.component.html',
    styleUrls: ['./update-change-list.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UpdateChangeListComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Update Generated On for the selected items?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Global data properties (populated from services)
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  // Full batch-select array from the originating grid (array of {row_id, data} entries)
  gridData: any = null;

  showModal: boolean = false;
  syncSaveDtls: any[] = [];
  save_json: any[] = [];
  logged_in_user: string = '';
  modalName: string = '';
  aggLimit: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private updateChangeListService: UpdateChangeListService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    // rowData = first/single row object (used as service fallback for field lookups)
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] ?? {}) : (rawRow || {});
    // gridData = full batch-select array [{row_id, data:{...}}] from CeHostDialog
    this.gridData = gridPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init(): void {
    try {
      const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
      const hasRowData = this.rowData && Object.keys(this.rowData).length > 0;
      const hasGridData = Array.isArray(this.gridData) && this.gridData.length > 0;

      if (hasFilterParams || hasRowData || hasGridData) {
        this.initializeData();
      }
    } catch (e) {
      console.error('Error in UpdateChangeList init:', e);
    }
  }

  initializeData(): void {
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }

    if (this.globalService?.modalScreen?.modal_name) {
      this.modalName = this.globalService.modalScreen.modal_name;
    }

    if (this.rowData) {
      this.processGridData();
    }

    this.fetchMaxCountEntities();
  }

  processGridData(): void {
    // For batch toolbar actions: gridData is the [{row_id, data:{...}}] array from the grid.
    // For single-row pencil clicks: gridData is not an array, fall back to rowData.
    const sourceData = (Array.isArray(this.gridData) && this.gridData.length > 0)
      ? this.gridData
      : this.rowData;

    if (sourceData == null) return;

    this.syncSaveDtls = [];
    this.save_json = [];

    const gridDataMultipleSelect = _.clone(sourceData);

    for (var key in gridDataMultipleSelect) {
      const entry = gridDataMultipleSelect[key];
      // Unwrap grid row node shape { data: {...} } or use entry directly
      const dataItem = (entry && typeof entry === 'object' && entry.data && typeof entry.data === 'object')
        ? entry.data
        : entry;

      const gridObj: any = {};
      gridObj["le_me_key"] = dataItem.LE_ME_KEY;
      gridObj["data_level"] = dataItem.DATA_LEVEL;
      gridObj["block_type"] = dataItem.BLOCK_TYPE;
      gridObj["group_obj_key"] = dataItem.GROUP_OBJ_KEY;
      gridObj["tax_year"] = dataItem.TAX_YEAR;
      this.syncSaveDtls.push(gridObj);

      this.save_json.push({
        "LE_ME_KEY": dataItem.LE_ME_KEY,
        "DATA_LEVEL": dataItem.DATA_LEVEL,
        "BLOCK_TYPE": dataItem.BLOCK_TYPE,
        "GROUP_OBJ_KEY": dataItem.GROUP_OBJ_KEY,
        "TAX_YEAR": dataItem.TAX_YEAR
      });
    }
  }

  fetchMaxCountEntities(): void {
    const params = {
      "action_code": 'mysfkb',
      "screen_key": this.globalService?.screen_key || ''
    };

    this.updateChangeListService.fetchMaxCountEntities(
      this.GENERAL_CONFIG.base_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.aggLimit = data.jsonObject[0].ATTRIB_VALUE;
      },
      (error: any) => {
        console.error('Error fetching max count entities:', error);
        this.aggLimit = '100';
      }
    );
  }

  onConfirm(): void {
    this.confirmSync();
  }

  cancel(): void {
    this.showModal = false;

    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });

    if (document.querySelector('gtw-update-change-list-ce')) {
      document.querySelector('gtw-update-change-list-ce')?.dispatchEvent(updateEvent);
    }
  }

  onCancel(): void {
    this.cancel();
  }

  confirmSync(): void {
    if (this.syncSaveDtls.length > parseInt(this.aggLimit)) {
      this.gtwSnackbarService.openSnackBar("Can't select more than " + this.aggLimit + " entities", 'gtw-snackbar--red');
      return;
    }

    const returnClobSettingsObj: any = {};
    returnClobSettingsObj['sso_id'] = this.logged_in_user;

    const message = "Submitted for Updating Generated On";

    this.sendDetails(returnClobSettingsObj, this.save_json, message);
  }

  sendDetails(returnClobSettingsObj: any, save_json: any[], message: string): void {
    this.modalConfig = { ...this.modalConfig, loading: true };

    this.updateChangeListService.updateGeneratedOn(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      save_json,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };

        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        console.error('Update Generated On error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while updating generated on. Please try again.', 'gtw-snackbar--red');
      }
    );
  }
}
